import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { Observable } from 'rxjs';

import { PoolDecline } from '@assetaccountant/api/models/pools/pool-decline';
import { Pool } from '../components/pool-setting-selection/model/pool';
import { REGISTER_PLACEHOLDER } from '../http-token.interceptor';
import { FinancialYear } from '../pipes/aa-fin-year.pipe';

export interface EnabledPool {
  code: string;
  name: string;
  regimeId: number;
  recommendTransfers: boolean;
  displayOrder: number;
  assetLimit: PoolLimit[];
  _isFeatureEnabled?: boolean;
}

export interface DateBasedPoolLimit {
  type: 'DateBasedPoolLimit';
  effectiveFrom: string;
}

export interface MinimumAmountPoolLimit {
  type: 'MinimumAmountPoolLimit';
  effectiveFrom: string;
  min: number;
}

export interface MaximumAmountPoolLimit {
  type: 'MaximumAmountPoolLimit';
  effectiveFrom: string;
  max: number;
}

export interface DisallowedPoolLimit {
  type: 'DisallowedPoolLimit';
  effectiveFrom: string;
}

export type PoolLimit =
  | DateBasedPoolLimit
  | MaximumAmountPoolLimit
  | MinimumAmountPoolLimit
  | DisallowedPoolLimit;

export interface PoolAddition {
  id: number;
  name: string;
  description: string;
  groupId: number;
  groupName: string;
  purchaseDate: string;
  firstUseDate: string;
  yearAdded: FinancialYear;
  originalCost: number;
}

export interface PoolTransfer {
  id: number;
  name: string;
  description: string;
  groupId: number;
  groupName: string;
  purchaseDate: string;
  firstUseDate: string;
  yearAdded?: FinancialYear;
  originalCost: number;
  writtenDownValue: number;
  depreciationMethodCode: string;
  depreciationMethodName: string;
  life: number;
}

export interface PoolBalancingAdjustment {
  id: number;
  name: string;
  description: string;
  purchaseDate: string;
  firstUseDate: string;
  yearAdded: FinancialYear;
  originalCost: number;
  terminationValue: number;
}

export interface PoolWriteOffResult {
  registerId: number;
  poolCode: string;
  successful: boolean;
  writeOffAmount?: number;
  messages: { [key: string]: string };
}

@Injectable({ providedIn: 'root' })
export class PoolsService {
  constructor(private http: HttpClient) {}

  getAllPools(registerId?: number) {
    return this.http.get<Pool[]>(
      `${environment.servicesApiUrl}/api/v2/registers/${registerId || REGISTER_PLACEHOLDER}/pools`,
    );
  }

  getEnabledPools(registerId?: number): Observable<EnabledPool[]> {
    return this.http.get<EnabledPool[]>(
      `${environment.servicesApiUrl}/api/register/${
        registerId || REGISTER_PLACEHOLDER
      }/enabled-pools`,
    );
  }

  getDeclineV2(poolCode: string) {
    return this.http.get<{ totals: PoolDecline[] }>(
      `${environment.servicesApiUrl}/api/v2/pool/${poolCode}`,
    );
  }

  getOpeningAssets(poolCode: string): Observable<PoolTransfer[]> {
    return this.http.get<PoolTransfer[]>(
      `${environment.servicesApiUrl}/api/pool/${poolCode}/opening-assets`,
    );
  }

  getClosingAssets(poolCode: string): Observable<PoolTransfer[]> {
    return this.http.get<PoolTransfer[]>(
      `${environment.servicesApiUrl}/api/pool/${poolCode}/closing-assets`,
    );
  }

  getBalancingAdjustments(poolCode: string) {
    return this.http.get<PoolBalancingAdjustment[]>(
      `${environment.servicesApiUrl}/api/pool/${poolCode}/balancing-adjustments`,
    );
  }

  getAdditions(poolCode: string) {
    return this.http.get<PoolAddition[]>(
      `${environment.servicesApiUrl}/api/pool/${poolCode}/additions`,
    );
  }

  getTransfers(poolCode: string) {
    return this.http.get<PoolTransfer[]>(
      `${environment.servicesApiUrl}/api/pool/${poolCode}/transfers`,
    );
  }

  getEligibleTransfers(poolCode: string) {
    return this.http.get<PoolTransfer[]>(
      `${environment.servicesApiUrl}/api/pool/${poolCode}/eligibletransfers`,
    );
  }

  addOpeningBalance({
    registerId,
    poolCode,
    effectiveDate,
    writtenDownValue,
  }: {
    registerId?: number;
    poolCode: string;
    effectiveDate: string;
    writtenDownValue: number;
  }) {
    return this.http.post<void>(
      `${environment.servicesApiUrl}/api/pool/${
        registerId || REGISTER_PLACEHOLDER
      }/${poolCode}/opening-balance`,
      {
        effectiveDate,
        writtenDownValue,
      },
    );
  }

  reverseOpeningBalance({ registerId, poolCode }: { registerId?: number; poolCode: string }) {
    return this.http.post<void>(
      `${environment.servicesApiUrl}/api/pool/${
        registerId || REGISTER_PLACEHOLDER
      }/${poolCode}/reverse-opening-balance`,
      {},
    );
  }
}
